import React, { Fragment, useEffect, useState } from "react";
import { useRespuesta } from "../../data/RespuestaContext";
import { useProyecto } from "../../data/ProyectoContext";
import {
  Button,
  Card,
  CardContent,
  Grid,
  styled,
  Box,
  Collapse,
} from "@mui/material";
import { useJobContext } from "../../hooks/Jobs";
import { config } from "../../config/configLabels";
import ExpandIcon from "./img/ExpandIcon.svg";
import MarkDownText from "../MarkDownText/MarkDownText";
import MarkDownEditor from "../MarkDownText/MarkDownEditor";

const Propuestas = ({ itemProyecto, indexPropuesta, color }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { etapa, tipoEstructura } = useJobContext();
  const { setRespuestaData, respuesta } = useRespuesta();
  const { enviarProyecto } = useProyecto();
  const [labels, setLabels] = useState([]);
  const [expanded, setExpanded] = useState(false);

  // markdown por propuesta
  const [markdown, setMarkdown] = useState("");
  const [editMode, setEditMode] = useState(false);

  const NumberBox = styled(Box)(({ theme }) => ({
    width: theme.spacing(10),
    backgroundColor: color,
    color: "white",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "3rem",
  }));


  //Si está en modo edición, está expandido
   useEffect(() => {
    if (editMode) {
      setExpanded(true);
    }
  }, [editMode]);

  // Cargar orden de labels desde config
  useEffect(() => {
    const load = () => {
      const labelsCfg = config[etapa]?.[tipoEstructura] || [];
      setLabels(labelsCfg);
    };
    if (itemProyecto) {
      load();
    }
  }, [itemProyecto, etapa, tipoEstructura]);

  // Construir Markdown unificado a partir de los campos
  useEffect(() => {
    if (!itemProyecto || labels.length === 0) return;

    // Si ya viene un markdown guardado, se usa.
    if (itemProyecto.textoEditado) {
      setMarkdown(itemProyecto.textoEditado);
      setIsLoading(false);
      return;
    }

    const md = labels
      .map((label) => {
        const valor = itemProyecto[label];
        if (!valor) return "";
        return `${valor}`;
      })
      .filter(Boolean)
      .join("\n\n");

    setMarkdown(md);
    setIsLoading(false);
  }, [itemProyecto, labels]);

  // Se guarda el markdown unificado dentro de la propuesta
  const handleSaveMarkdown = () => {
    if (!respuesta || !respuesta.propuestas) {
      console.log("No hay respuesta o propuestas disponibles");
      return;
    }

    const nuevasPropuestas = [...respuesta.propuestas];
    nuevasPropuestas[indexPropuesta] = {
      ...nuevasPropuestas[indexPropuesta],
      textoEditado: markdown,
    };

    const updatePropuesta = {
      ...respuesta,
      propuestas: nuevasPropuestas,
    };

    setRespuestaData(updatePropuesta);
    setEditMode(false);
  };


   const getPreview = (text, limit = 300) => {
    if (!text) return "";
    if (text.length <= limit) return text;
    return text.slice(0, limit) + "…";
  };

  const handleCancelMarkdown = () => {
    // Volver al valor original (si existe)
    if (itemProyecto.textoEditado) {
      setMarkdown(itemProyecto.textoEditado);
    } else {
      // reconstruir desde los campos de la propuesta
      const md = labels
        .map((label) => {
          const valor = itemProyecto[label];
          if (!valor) return "";
          
          return `${valor}`;
        })
        .filter(Boolean)
        .join("\n\n");
      setMarkdown(md);
    }
    setEditMode(false);
  };

  const handleSelect = (item) => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    const proyectoSeleccionado = item;
    enviarProyecto(proyectoSeleccionado, etapa);
  };

  return (
     <Grid key={indexPropuesta} item xs={12} md={12}>
      {isLoading && <>...cargando</>}
      {!isLoading && (
        <Card
          key={indexPropuesta}
          sx={{
            display: "flex",
            m: 2,
            width: "98%",
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <NumberBox
            sx={{ fontWeight: "bold", fontSize: "65px", color: "#336B87" }}
          >
            {indexPropuesta + 1}
          </NumberBox>

          <CardContent sx={{ fontFamily: "Montserrat", width: "100%" }}>
            {/* Header de la card: botón colapsar / expandir */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              {!editMode && (
                <Button
                  size="small"
                  onClick={() => setExpanded((prev) => !prev)}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    borderRadius: 20,
                    borderColor: "#336B87",
                    color: "#336B87",
                  }}
                  variant="outlined"
                >
                  {expanded ? "Ver menos" : "Ver más"}
                </Button>
              )}
            </Box>

            {/* ---- Vista NO editable ---- */}
            {!editMode && (
              <>
                {/* Preview cuando está colapsado */}
                {!expanded && (
                  <Box sx={{ mb: 2 }}>
                    <MarkDownText
                      data={getPreview(markdown)}
                      img={null}
                    />
                  </Box>
                )}

                {/* Collapse */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Box sx={{ mb: 2 }}>
                    <MarkDownText data={markdown} img={null} />
                  </Box>
                  <Button
                    variant="outlined"
                    sx={{
                      borderRadius: "20px",
                      borderColor: "#336B87",
                      color: "#336B87",
                      mr: 1,
                    }}
                    startIcon={
                      <img
                        src={ExpandIcon}
                        alt="Icono"
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                    onClick={() => setEditMode(true)}
                  >
                    Editar propuesta
                  </Button>
                </Collapse>
              </>
            )}

            {/* Vista editable */}
            {editMode && (
              <>
                <MarkDownEditor data={markdown} setEditedValue={setMarkdown} />
                <Box
                  m={2}
                  display="flex"
                  width="100%"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    onClick={handleSaveMarkdown}
                    sx={{
                      borderRadius: 5,
                      fontFamily: "Montserrat",
                      fontWeight: 700,
                      textTransform: "none",
                      bgcolor: "#336B87",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#1FC16B",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Guardar
                  </Button>
                  <Button
                    sx={{
                      ml: 2,
                      borderRadius: 5,
                      fontFamily: "Montserrat",
                      fontWeight: 700,
                      textTransform: "none",
                      bgcolor: "#336B87",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#ff0000ff",
                        boxShadow: "none",
                      },
                    }}
                    onClick={handleCancelMarkdown}
                  >
                    Cancelar
                  </Button>
                </Box>
              </>
            )}

            {/* Botón seleccionar */}
            <Button
              variant="contained"
              color="success"
              sx={{
                m: "auto",
                mt: 5,
                p: "5px",
                width: "50%",
                left: "20%",
                right: "15%",
                borderRadius: 5,
                backgroundColor: "#336B87",
                "&:hover": {
                  backgroundColor: "#336B82",
                },
              }}
              onClick={() => handleSelect(itemProyecto)}
              data-amplify-analytics-on="click"
              data-amplify-analytics-name={`click-on-seleccionarPropuesta`}
              data-amplify-analytics-attrs={`onClick:handleSelect etapa=${etapa}, tipoEstructura:${tipoEstructura}`}
            >
              Seleccionar
            </Button>
          </CardContent>
        </Card>
      )}
    </Grid>
  );
};

export default Propuestas;
